import type { SharingOption } from '~/components/ShareContent';
import type { Property } from '~/core/entities/property';

import { onClickOutside, useClipboard } from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { concatUrlPath } from '~/utils';

import { CopyToastDescription } from '~/components/CopyToastDescription';
import { useToast } from '~/components/ui/toast';

interface UsePropertyHeroShareOptions {
	property: Ref<Property | null>;
}

export function usePropertyHeroShare({
	property
}: UsePropertyHeroShareOptions) {
	const { ORIGIN } = useRuntimeConfig().public;
	const router = useRouter();
	const { copy } = useClipboard();
	const { toast } = useToast();
	const { trackSharePropertyClicked } = useEvents();

	const showShareContent = ref(false);
	const shareContentRef = ref<HTMLElement | null>(null);
	const shareContentButton = ref<HTMLElement | null>(null);

	const shareUrl = computed<string>(() =>
		concatUrlPath(ORIGIN, router.currentRoute.value.fullPath)
	);

	onClickOutside(shareContentRef, () => (showShareContent.value = false), {
		ignore: [shareContentButton]
	});

	function toggleShareContent() {
		showShareContent.value = !showShareContent.value;
	}

	function shareMobile() {
		const data = {
			title: property.value?.ad.title,
			url: shareUrl.value
		};

		if (navigator?.share && navigator?.canShare(data)) {
			navigator.share(data);
		} else {
			copy(shareUrl.value);
			toast({
				description: h(CopyToastDescription)
			});
		}
	}

	function handleShareClicked(option: SharingOption) {
		trackSharePropertyClicked({
			share_whatsapp: option.label === 'WhatsApp',
			share_facebook: option.label === 'Facebook',
			copy_link: false,
			share_email: option.label === 'Via e-mail',
			share_twitter: option.label === 'Twitter'
		});
	}

	return {
		showShareContent,
		shareContentRef,
		shareContentButton,
		shareUrl,
		toggleShareContent,
		shareMobile,
		handleShareClicked
	};
}
